import { useAppointmentsStore, useCampaignsStore, useSettingsStore, useAuthStore } from '@/stores';
import { formatDateForFilename } from '@/utils/date';

export function exportStandaloneHtml() {
  const appointments = useAppointmentsStore.getState().items;
  const selectedDate = useAppointmentsStore.getState().selectedDate;
  const campaigns = useCampaignsStore.getState().clients;
  const settings = useSettingsStore.getState();
  const provider = useAuthStore.getState().provider;

  const businessName = provider?.data?.attributes?.name || 'Fresha Hub';
  const exportDate = formatDateForFilename(selectedDate);
  const fileName = `fresha-snapshot-${exportDate}.html`;

  const payload = {
    exportedAt: new Date().toISOString(),
    businessName,
    selectedDate,
    appointments,
    campaigns,
    settings,
  };

  const serializedData = JSON.stringify(payload).replace(/</g, '\\u003c');

  const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${businessName} - Snapshot ${exportDate}</title>
  <style>
    :root {
      --bg: #0f172a;
      --surface: #1e293b;
      --surface-sec: #334155;
      --text: #f1f5f9;
      --text-muted: #94a3b8;
      --primary: #3b527d;
      --primary-light: #5a7ab0;
      --success: #10b981;
      --warning: #f59e0b;
      --danger: #ef4444;
      --border: #334155;
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; background: var(--bg); color: var(--text); padding: 16px; max-width: 800px; margin: 0 auto; line-height: 1.5; }
    header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 1px solid var(--border); }
    h1 { font-size: 1.25rem; font-weight: 700; color: #fff; }
    .tabs { display: flex; gap: 8px; margin-bottom: 16px; }
    .tab-btn { flex: 1; padding: 8px 12px; background: var(--surface); border: 1px solid var(--border); color: var(--text-muted); border-radius: 8px; font-weight: 600; cursor: pointer; font-size: 0.85rem; transition: all 0.2s; }
    .tab-btn.active { background: var(--primary); color: #fff; border-color: var(--primary); }
    .search-box { width: 100%; padding: 10px 14px; background: var(--surface); border: 1px solid var(--border); color: var(--text); border-radius: 8px; margin-bottom: 16px; font-size: 0.9rem; outline: none; }
    .search-box:focus { border-color: var(--primary-light); }
    .card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 14px; margin-bottom: 12px; transition: border-color 0.2s, background-color 0.2s; }
    .card.processed { border-color: rgba(16, 185, 129, 0.4); background: rgba(16, 185, 129, 0.05); }
    .card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
    .name { font-weight: 600; font-size: 0.95rem; color: #fff; }
    .badge { font-size: 0.75rem; padding: 2px 8px; border-radius: 999px; font-weight: 600; text-transform: capitalize; }
    .badge-success { background: rgba(16, 185, 129, 0.2); color: var(--success); }
    .badge-warning { background: rgba(245, 158, 11, 0.2); color: var(--warning); }
    .badge-danger { background: rgba(239, 68, 68, 0.2); color: var(--danger); }
    .badge-info { background: rgba(59, 82, 125, 0.3); color: #93c5fd; }
    .details { font-size: 0.8rem; color: var(--text-muted); margin-bottom: 10px; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .btn { display: inline-flex; align-items: center; gap: 4px; padding: 6px 12px; border-radius: 6px; font-size: 0.75rem; font-weight: 600; text-decoration: none; border: none; cursor: pointer; transition: all 0.2s; }
    .btn-wa { background: rgba(16, 185, 129, 0.2); color: #34d399; }
    .btn-wa:hover { background: rgba(16, 185, 129, 0.3); }
    .btn-sec { background: var(--surface-sec); color: var(--text); }
    .btn-sec:hover { background: #475569; }
    .btn-processed { background: var(--surface-sec); color: var(--text-muted); }
    .card.processed .btn-processed { background: rgba(16, 185, 129, 0.2); color: var(--success); }
    .empty { text-align: center; padding: 40px; color: var(--text-muted); font-size: 0.9rem; }
  </style>
</head>
<body>
  <header>
    <div>
      <h1>${businessName}</h1>
      <p style="font-size:0.75rem; color:var(--text-muted);">Snapshot del ${exportDate}</p>
    </div>
    <span style="font-size:0.75rem; background:var(--surface-sec); padding:4px 8px; border-radius:6px; font-weight:500;">100% Offline</span>
  </header>

  <div class="tabs">
    <button class="tab-btn active" onclick="switchTab('appointments')">Citas (<span id="count-appts">0</span>)</button>
    <button class="tab-btn" onclick="switchTab('campaigns')">Campañas (<span id="count-camps">0</span>)</button>
  </div>

  <input type="text" id="search" class="search-box" placeholder="Buscar por nombre, teléfono, servicio..." oninput="render()">

  <div id="content"></div>

  <script>
    const DATA = ${serializedData};
    let activeTab = 'appointments';
    const PROCESSED_KEY = 'fresha_snapshot_processed_' + DATA.selectedDate;
    let processed = new Set(JSON.parse(localStorage.getItem(PROCESSED_KEY) || '[]'));

    function escapeHtml(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    function toggleProcessed(id) {
      if (processed.has(id)) processed.delete(id);
      else processed.add(id);
      localStorage.setItem(PROCESSED_KEY, JSON.stringify([...processed]));
      render();
    }

    function switchTab(tab) {
      activeTab = tab;
      document.querySelectorAll('.tab-btn').forEach((b, idx) => {
        b.classList.toggle('active', (tab === 'appointments' && idx === 0) || (tab === 'campaigns' && idx === 1));
      });
      render();
    }

    function copyText(txt) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(txt).then(() => {
          alert('Copiado: ' + txt);
        }).catch(() => {
          prompt('Copiar texto:', txt);
        });
      } else {
        const ta = document.createElement('textarea');
        ta.value = txt;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        alert('Copiado: ' + txt);
      }
    }

    function copyAppointmentPhone(id) {
      const a = (DATA.appointments || []).find(x => String(x.id) === String(id));
      if (a && a.customerPhone) copyText(a.customerPhone);
    }

    function copyAppointmentMessage(id) {
      const a = (DATA.appointments || []).find(x => String(x.id) === String(id));
      if (a && a.whatsappMessage) copyText(a.whatsappMessage);
    }

    function copyCampaignPhone(id) {
      const c = (DATA.campaigns || []).find(x => String(x.id) === String(id));
      if (c && c.phone) copyText(c.phone);
    }

    function copyCampaignMessage(id) {
      const c = (DATA.campaigns || []).find(x => String(x.id) === String(id));
      if (c && c.whatsappMessage) copyText(c.whatsappMessage);
    }

    function render() {
      const q = (document.getElementById('search').value || '').trim().toLowerCase();
      const container = document.getElementById('content');
      const appointmentsList = DATA.appointments || [];
      const campaignsList = DATA.campaigns || [];
      document.getElementById('count-appts').textContent = appointmentsList.length;
      document.getElementById('count-camps').textContent = campaignsList.length;

      const sym = (DATA.settings && DATA.settings.currencyCode === 'EUR') ? '€' : '$';

      if (activeTab === 'appointments') {
        const list = appointmentsList.filter(a =>
          !q ||
          (a.customerName && a.customerName.toLowerCase().includes(q)) ||
          (a.serviceName && a.serviceName.toLowerCase().includes(q)) ||
          (a.customerPhone && a.customerPhone.includes(q))
        );
        if (list.length === 0) {
          container.innerHTML = '<div class="empty">No se encontraron citas</div>';
          return;
        }
        container.innerHTML = list.map(a => {
          const isProc = processed.has(a.id);
          const priceText = a.totalAmount != null ? ' • ' + sym + Number(a.totalAmount).toFixed(2) : '';
          return \`
            <div class="card \${isProc ? 'processed' : ''}">
              <div class="card-header">
                <div>
                  <div class="name">\${escapeHtml(a.customerName)}</div>
                  <div class="details">\${escapeHtml(a.startTime || '')} • \${escapeHtml(a.serviceName || '')}\${priceText} \${a.employeeName ? '• ' + escapeHtml(a.employeeName) : ''}</div>
                </div>
                <span class="badge badge-info">\${escapeHtml(a.status || '')}</span>
              </div>
              <div class="actions">
                \${a.whatsappUrl ? \`<a href="\${a.whatsappUrl}" target="_blank" rel="noopener noreferrer" class="btn btn-wa">💬 WhatsApp</a>\` : ''}
                \${a.customerPhone ? \`<button onclick="copyAppointmentPhone('\${a.id}')" class="btn btn-sec">📞 \${escapeHtml(a.customerPhone)}</button>\` : ''}
                \${a.whatsappMessage ? \`<button onclick="copyAppointmentMessage('\${a.id}')" class="btn btn-sec">📋 Mensaje</button>\` : ''}
                <button onclick="toggleProcessed('\${a.id}')" class="btn btn-processed">\${isProc ? '✅ Listo' : 'Marcar'}</button>
              </div>
            </div>
          \`;
        }).join('');
      } else {
        const list = campaignsList.filter(c =>
          !q ||
          (c.name && c.name.toLowerCase().includes(q)) ||
          (c.phone && c.phone.includes(q))
        );
        if (list.length === 0) {
          container.innerHTML = '<div class="empty">No se encontraron clientes</div>';
          return;
        }
        container.innerHTML = list.map(c => {
          const isProc = processed.has(c.id);
          const debtText = c.outstandingAmount > 0 ? ' • Deuda: ' + sym + Number(c.outstandingAmount).toFixed(2) : '';
          return \`
            <div class="card \${isProc ? 'processed' : ''}">
              <div class="card-header">
                <div>
                  <div class="name">\${escapeHtml(c.name)}</div>
                  <div class="details">\${c.daysAbsent || 0} días ausente • Citas: \${c.totalAppointments || 0} • Ventas: \${sym}\${Number(c.totalSales || 0).toFixed(2)}\${debtText}</div>
                </div>
                <span class="badge badge-warning">\${escapeHtml(c.segment || '')}</span>
              </div>
              <div class="actions">
                \${c.whatsappUrl ? \`<a href="\${c.whatsappUrl}" target="_blank" rel="noopener noreferrer" class="btn btn-wa">💬 WhatsApp</a>\` : ''}
                \${c.phone ? \`<button onclick="copyCampaignPhone('\${c.id}')" class="btn btn-sec">📞 \${escapeHtml(c.phone)}</button>\` : ''}
                \${c.whatsappMessage ? \`<button onclick="copyCampaignMessage('\${c.id}')" class="btn btn-sec">📋 Mensaje</button>\` : ''}
                <button onclick="toggleProcessed('\${c.id}')" class="btn btn-processed">\${isProc ? '✅ Listo' : 'Marcar'}</button>
              </div>
            </div>
          \`;
        }).join('');
      }
    }

    render();
  </script>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
